<template>
  <div>

    <b-sidebar
      id="sidebar-add-new-event"
      sidebar-class="sidebar-lg"
      :visible="isEventHandlerSidebarActive"
      bg-variant="white"
      shadow
      backdrop
      no-header
      right
      @change="(val) => $emit('update:is-event-handler-sidebar-active', val)"
    >

      <template #default="{ hide }">
        <!-- Header -->
        <div class="d-flex justify-content-between align-items-center content-sidebar-header px-2 py-1">
          <h5 class="mb-0">
            {{ eventLocal.id ? 'Update': 'Add' }} Event
          </h5>
          <div>
            <feather-icon
              v-if="eventLocal.id"
              icon="TrashIcon"
              class="cursor-pointer"
              @click="$emit('remove-event'); hide();"
            />
            <feather-icon
              class="ml-1 cursor-pointer"
              icon="XIcon"
              size="16"
              @click="hide"
            />
          </div>
        </div>

        <div class="loading_page" v-if="requestStatus">
          <orbit-spinner
              :animation-duration="1000"
              :size="100"
              :color="'#0e96b1'"
          />
        </div>


        <!-- Body -->
          <!-- Form -->
          <b-form
            class="p-2"
            @submit.prevent="onSubmit"
            @reset.prevent="">

            <!-- Title -->

              <b-form-group
                label="Title"
                label-for="plan-title">
                <b-form-input
                  id="plan-title"
                  v-model="eventLocal.title"
                  autofocus :state="null" trim
                  :placeholder="$t('Plan Title')"
                />
                <b-form-invalid-feedback>
                </b-form-invalid-feedback>
              </b-form-group>


            <!-- Start Date -->

              <b-form-group
                label="Start Date"
                label-for="start-date"
                :state="null">
                <flat-pickr
                  v-model="eventLocal.start"
                  class="form-control"
                  :config="{ enableTime: true, dateFormat: 'Y-m-d H:i'}"/>
                <b-form-invalid-feedback :state="null">
                </b-form-invalid-feedback>
              </b-form-group>


            <!-- End Date -->
              <b-form-group
                label="End Date"
                label-for="end-date"
                :state="null">
                <flat-pickr
                  v-model="eventLocal.end"
                  class="form-control"
                  :config="{ enableTime: true, dateFormat: 'Y-m-d H:i'}"/>
                <b-form-invalid-feedback :state="null">
                </b-form-invalid-feedback>
              </b-form-group>


            <b-form-group
                :label="$t('Plan Type')"
                label-for="calendar"
                :state="null">

              <v-select
                  v-model="eventLocal.extendedProps.calendar"
                  :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                  :options="calendarOptions"
                  label="label"
                  input-id="calendar">

                <template #option="{ color, label }">
                  <div
                      class="rounded-circle d-inline-block mr-50"
                      :class="`bg-${color}`"
                      style="height:10px;width:10px"
                  />
                  <span> {{ label }}</span>
                </template>

                <template #selected-option="{ color, label }">
                  <div
                      class="rounded-circle d-inline-block mr-50"
                      :class="`bg-${color}`"
                      style="height:10px;width:10px"
                  />
                  <span> {{ label }}</span>
                </template>
              </v-select>

              <b-form-invalid-feedback :state="null">
              </b-form-invalid-feedback>
            </b-form-group>


            <!-- Repeat -->
            <b-form-group>
              <b-form-checkbox
                  v-model="showRepeat"
                  name="check-button"
                  switch
                  inline>
                {{ $t('Repeat')}}
              </b-form-checkbox>
            </b-form-group>



            <transition name="slide-fade">

              <b-form-group

                  :label="$t('Repeat Every')"
                  label-for="repeat"
                  :state="null">

                <v-select
                    :disabled="showRepeat"
                    v-model="eventLocal.repeat"
                    :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                    :options="repeatOptions"
                    input-id="repeat">
                </v-select>

                <b-form-invalid-feedback :state="null">
                </b-form-invalid-feedback>
              </b-form-group>


            </transition>




            <!-- All Day -->
            <b-form-group>
              <b-form-checkbox
                v-model="eventLocal.allDay"
                name="check-button"
                switch
                inline>
                {{ $t('All Day')}}
              </b-form-checkbox>
            </b-form-group>

            <!-- Form Actions -->
            <div class="d-flex mt-2">
              <b-button
                v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                variant="primary"
                class="mr-2"
                type="submit"
              >
                {{ eventLocal.id ? 'Update' : 'Add ' }}
              </b-button>
              <b-button
                v-ripple.400="'rgba(186, 191, 199, 0.15)'"
                type="reset"
                variant="outline-secondary">
                {{ $t('Reset')}}
              </b-button>
            </div>
          </b-form>
      </template>
    </b-sidebar>
  </div>
</template>

<script>
import {
  BSidebar, BForm, BFormGroup, BFormInput, BFormCheckbox, BAvatar, BFormTextarea, BButton, BFormInvalidFeedback,
} from 'bootstrap-vue'
import vSelect from 'vue-select'
import flatPickr from 'vue-flatpickr-component'
import Ripple from 'vue-ripple-directive'
import { ValidationProvider, ValidationObserver } from 'vee-validate'
import { required, email, url } from '@validations'
import formValidation from '@core/comp-functions/forms/form-validation'
import { ref, toRefs } from '@vue/composition-api'
import useCalendarEventHandler from './useCalendarEventHandler'
import { OrbitSpinner } from 'epic-spinners'

export default {
  components: {
    BButton,
    BSidebar,
    BForm,
    BFormGroup,
    BFormInput,
    BFormCheckbox,
    BFormTextarea,
    BAvatar,
    vSelect,
    flatPickr,
    ValidationProvider,
    BFormInvalidFeedback,
    ValidationObserver,
    OrbitSpinner
  },
  directives: {
    Ripple,
  },
  model: {
    prop: 'isEventHandlerSidebarActive',
    event: 'update:is-event-handler-sidebar-active',
  },
  props: {
    isEventHandlerSidebarActive: {
      type: Boolean,
      required: true,
    },
    event: {
      type: Object,
      required: true,
    },
    clearEventData: {
      type: Function,
      required: true,
    },
  },

  setup(props, { emit }) {
    /*
     ? This is handled quite differently in SFC due to deadlock of `useFormValidation` and this composition function.
     ? If we don't handle it the way it is being handled then either of two composition function used by this SFC get undefined as one of it's argument.
     * The Trick:

     * We created reactive property `clearFormData` and set to null so we can get `resetEventLocal` from `useCalendarEventHandler` composition function.
     * Once we get `resetEventLocal` function which is required by `useFormValidation` we will pass it to `useFormValidation` and in return we will get `clearForm` function which shall be original value of `clearFormData`.
     * Later we just assign `clearForm` to `clearFormData` and can resolve the deadlock. 😎

     ? Behind The Scene
     ? When we passed it to `useCalendarEventHandler` for first time it will be null but right after it we are getting correct value (which is `clearForm`) and assigning that correct value.
     ? As `clearFormData` is reactive it is being changed from `null` to corrent value and thanks to reactivity it is also update in `useCalendarEventHandler` composition function and it is getting correct value in second time and can work w/o any issues.
    */
    const clearFormData = ref(null)

    const showRepeat = ref(false)

    const {
      eventLocal,
      resetEventLocal,
      calendarOptions,
      repeatOptions,
      requestStatus,

      // UI
      onSubmit,
    } = useCalendarEventHandler(toRefs(props), clearFormData, emit)

    // const {
    //   refFormObserver,
    //   getValidationState,
    //   resetForm,
    //   clearForm,
    // } = formValidation(resetEventLocal, props.clearEventData)

    // clearFormData.value = clearForm

    return {
      // Add New Event
      eventLocal,
      calendarOptions,
      onSubmit,

      // Form Validation
      // resetForm,
      // refFormObserver,
      // getValidationState,
      requestStatus,
      repeatOptions,
      showRepeat
    }
  },
}
</script>

<style lang="scss">
@import '@core/scss/vue/libs/vue-select.scss';
@import '@core/scss/vue/libs/vue-flatpicker.scss';

.slide-fade-enter-active {
  transition: all .8s ease;
}
.slide-fade-leave-active {
  transition: all .8s cubic-bezier(1.0, 0.5, 0.8, 1.0);
}
.slide-fade-enter, .slide-fade-leave-to
  /* .slide-fade-leave-active below version 2.1.8 */ {
  transform: translateZ(10px);
  opacity: 0;
}


[dir] .vs--disabled .vs__dropdown-toggle,
[dir] .vs--disabled .vs__clear,
[dir] .vs--disabled .vs__search,
[dir] .vs--disabled .vs__selected,
[dir] .vs--disabled .vs__open-indicator {
  background-color: #283046;
}

</style>
